import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Pencil, Route, RefreshCw, Search, X, Save, AlertCircle,
  ArrowRight, Clock, MapPin, Euro, Car, Plus, Sparkles, FileText, Trash2
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminRoutesPage() {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [fromFilter, setFromFilter] = useState("all");
  const [toFilter, setToFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [clearingPrices, setClearingPrices] = useState(false);
  
  // SEO generation state
  const [seoGenerating, setSeoGenerating] = useState(false);
  const [seoStatus, setSeoStatus] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  
  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    from_slug: "",
    to_slug: "",
    distance: "",
    duration: "",
    description: ""
  });
  
  // Form data
  const [formData, setFormData] = useState({
    distance: "",
    duration: "",
    description: "",
    vehicle_prices: []
  });

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem("adminToken");
    setLoading(true);
    setError("");
    
    try {
      const [routesRes, locationsRes, vehiclesRes] = await Promise.all([
        fetch(`${API_BASE}/routes/admin/list`, {
          headers: { "Authorization": `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/locations`),
        fetch(`${API_BASE}/vehicles`)
      ]);

      if (!routesRes.ok) {
        if (routesRes.status === 401) {
          localStorage.removeItem("adminToken");
          navigate("/admin/login");
          return;
        }
        throw new Error("Failed to fetch routes");
      }

      const [routesData, locationsData, vehiclesData] = await Promise.all([
        routesRes.json(),
        locationsRes.json(),
        vehiclesRes.json()
      ]);

      setRoutes(routesData);
      setLocations(locationsData);
      setVehicles(vehiclesData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate SEO content for all routes
  const generateAllSeo = async () => {
    const token = localStorage.getItem("adminToken");
    setSeoGenerating(true);
    setSeoStatus({ message: "Starting SEO generation..." });

    try {
      const res = await fetch(`${API_BASE}/seo/generate-all`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || "Failed to start generation");
      }

      setSeoStatus({ message: data.message, total: data.count, completed: 0 });
      
      // Poll for status
      const pollStatus = setInterval(async () => {
        try {
          const statusRes = await fetch(`${API_BASE}/seo/status`, {
            headers: { "Authorization": `Bearer ${token}` }
          });
          const status = await statusRes.json();
          
          setSeoStatus({
            message: status.is_running ? `Generating: ${status.current_route}` : "Generation complete!",
            total: status.total,
            completed: status.completed,
            errors: status.errors
          });

          if (!status.is_running) {
            clearInterval(pollStatus);
            setSeoGenerating(false);
            fetchData(); // Refresh routes
          }
        } catch (e) {
          console.error("Status poll error:", e);
        }
      }, 2000);

    } catch (err) {
      setSeoStatus({ message: `Error: ${err.message}`, error: true });
      setSeoGenerating(false);
    }
  };

  // Generate SEO for a single route
  const generateSeoForRoute = async (routeId) => {
    const token = localStorage.getItem("adminToken");
    setSaving(true);
    setFormError("");

    try {
      const res = await fetch(`${API_BASE}/seo/generate-one/${routeId}`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || "Failed to generate SEO");
      }

      // Update form with generated content
      setFormData(prev => ({
        ...prev,
        seo_title: data.seo_data.seo_title,
        seo_description: data.seo_data.seo_description,
        seo_content: data.seo_data.seo_content
      }));

    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (route) => {
    setEditingRoute(route);
    
    // Build vehicle_prices array with all vehicles
    const existingPrices = route.vehicle_prices || [];
    const vehiclePrices = vehicles.map(v => {
      const existing = existingPrices.find(vp => vp.vehicle_id === v.vehicle_id);
      return {
        vehicle_id: v.vehicle_id,
        vehicle_name: v.type,
        price: existing ? existing.price : 0,
        is_custom: existing ? existing.is_custom === true : false,
        calculated_price: existing ? existing.price : 0
      };
    });
    
    setFormData({
      distance: route.distance || "",
      duration: route.duration || "",
      description: route.description || "",
      vehicle_prices: vehiclePrices,
      // SEO fields
      seo_title: route.seo_title || "",
      seo_description: route.seo_description || "",
      seo_content: route.seo_content || ""
    });
    setFormError("");
    setShowModal(true);
  };

  const handleVehiclePriceChange = (vehicleId, price) => {
    setFormData(prev => ({
      ...prev,
      vehicle_prices: prev.vehicle_prices.map(vp => {
        if (vp.vehicle_id === vehicleId) {
          const newPrice = parseInt(price) || 0;
          // Mark as custom if price differs from calculated
          return { 
            ...vp, 
            price: newPrice,
            is_custom: newPrice > 0 && newPrice !== vp.calculated_price
          };
        }
        return vp;
      })
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");
    setSaving(true);
    setFormError("");

    try {
      // Only include custom prices (non-zero and marked as custom)
      const customPrices = formData.vehicle_prices
        .filter(vp => vp.price > 0 && vp.is_custom)
        .map(vp => ({
          vehicle_id: vp.vehicle_id,
          price: vp.price
        }));

      const payload = {
        distance: formData.distance || null,
        duration: formData.duration || null,
        description: formData.description || null,
        vehicle_prices: customPrices,
        // SEO fields - send empty string to clear, not null (null gets filtered out)
        seo_title: formData.seo_title?.trim() || "",
        seo_description: formData.seo_description?.trim() || "",
        seo_content: formData.seo_content?.trim() || ""
      };

      const res = await fetch(`${API_BASE}/routes/${editingRoute.route_id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to update route");
      }

      await fetchData();
      setShowModal(false);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Create route handler
  const handleCreateRoute = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");
    setSaving(true);
    setFormError("");

    try {
      if (!createFormData.from_slug || !createFormData.to_slug) {
        throw new Error("Please select both From and To locations");
      }
      
      if (createFormData.from_slug === createFormData.to_slug) {
        throw new Error("From and To locations cannot be the same");
      }

      const res = await fetch(`${API_BASE}/routes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(createFormData)
      });

      if (!res.ok) {
        let errorMessage = "Failed to create route";
        try {
          const errData = await res.json();
          errorMessage = errData.detail || errorMessage;
        } catch (parseError) {
          console.error("Error parsing response:", parseError);
        }
        throw new Error(errorMessage);
      }

      await fetchData();
      setShowCreateModal(false);
      setCreateFormData({
        from_slug: "",
        to_slug: "",
        distance: "",
        duration: "",
        description: ""
      });
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Clear all custom prices
  const handleClearAllPrices = async () => {
    if (!window.confirm("Are you sure you want to clear ALL custom prices?\n\nAll routes will use calculated prices based on vehicle scales.\n\nThis cannot be undone.")) {
      return;
    }
    
    const token = localStorage.getItem("adminToken");
    setClearingPrices(true);
    
    try {
      const res = await fetch(`${API_BASE}/routes/admin/clear-custom-prices`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to clear prices");
      }
      
      const data = await res.json();
      alert(`✅ ${data.message}\n\n${data.routes_updated} routes updated.`);
      await fetchData();
    } catch (err) {
      alert(`❌ Error: ${err.message}`);
    } finally {
      setClearingPrices(false);
    }
  };

  // Get unique from/to locations
  const fromLocations = [...new Set(routes.map(r => r.from_slug))].map(slug => {
    const loc = locations.find(l => l.slug === slug);
    return { slug, name: loc?.name || slug, region: loc?.region };
  });

  const toLocations = [...new Set(routes.map(r => r.to_slug))].map(slug => {
    const loc = locations.find(l => l.slug === slug);
    return { slug, name: loc?.name || slug, region: loc?.region };
  });

  // Get unique regions from locations
  const availableRegions = [...new Set(locations.map(l => l.region).filter(Boolean))].sort();

  // Helper to get region for a route (based on to_slug location)
  const getRouteRegion = (route) => {
    const toLoc = locations.find(l => l.slug === route.to_slug);
    return toLoc?.region || null;
  };

  // Filter routes
  const filteredRoutes = routes.filter(route => {
    const matchesSearch = !searchTerm || 
      route.from_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      route.to_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      route.route_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFrom = fromFilter === "all" || route.from_slug === fromFilter;
    const matchesTo = toFilter === "all" || route.to_slug === toFilter;
    const matchesRegion = regionFilter === "all" || getRouteRegion(route) === regionFilter;
    return matchesSearch && matchesFrom && matchesTo && matchesRegion;
  });

  // Parse distance helper
  const parseDistance = (distanceStr) => {
    if (!distanceStr) return 0;
    const num = parseFloat(distanceStr.replace(/[^\d.]/g, ''));
    return isNaN(num) ? 0 : num;
  };

  // Sort routes
  const sortedRoutes = [...filteredRoutes].sort((a, b) => {
    switch (sortBy) {
      case "distance-asc":
        return parseDistance(a.distance) - parseDistance(b.distance);
      case "distance-desc":
        return parseDistance(b.distance) - parseDistance(a.distance);
      case "price-asc":
        return (a.min_price || 0) - (b.min_price || 0);
      case "price-desc":
        return (b.min_price || 0) - (a.min_price || 0);
      case "name":
      default:
        return `${a.from_name} ${a.to_name}`.localeCompare(`${b.from_name} ${b.to_name}`);
    }
  });

  // Get min price for display
  const getMinPrice = (route) => {
    if (route.min_price) return route.min_price;
    const prices = (route.vehicle_prices || []).map(vp => vp.price).filter(p => p > 0);
    return prices.length > 0 ? Math.min(...prices) : route.base_price;
  };

  // Count routes with SEO content
  const routesWithSeo = routes.filter(r => r.seo_content && r.seo_content.trim().length > 0).length;
  const routesWithoutSeo = routes.length - routesWithSeo;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-routes">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Routes</h1>
            <p className="text-slate-500">Manage transfer routes and vehicle pricing</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">{routes.length} total routes</span>
            {routesWithoutSeo > 0 && (
              <Button 
                onClick={generateAllSeo} 
                disabled={seoGenerating}
                variant="outline"
                className="border-green-500 text-green-600 hover:bg-green-50"
                data-testid="generate-seo-btn"
              >
                {seoGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate SEO ({routesWithoutSeo})
                  </>
                )}
              </Button>
            )}
            <Button onClick={() => { setFormError(""); setShowCreateModal(true); }} data-testid="create-route-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Route
            </Button>
          </div>
        </div>

        {/* SEO Generation Status */}
        {seoStatus && (
          <div className={`rounded-lg p-4 ${seoStatus.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {seoGenerating ? (
                  <RefreshCw className="w-4 h-4 animate-spin text-green-600" />
                ) : (
                  <Sparkles className="w-4 h-4 text-green-600" />
                )}
                <span className={seoStatus.error ? 'text-red-700' : 'text-green-700'}>{seoStatus.message}</span>
              </div>
              {seoStatus.total > 0 && (
                <span className="text-sm text-green-600">{seoStatus.completed}/{seoStatus.total} completed</span>
              )}
            </div>
            {!seoGenerating && seoStatus.completed > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="mt-2 text-green-600"
                onClick={() => setSeoStatus(null)}
              >
                Dismiss
              </Button>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard 
            label="Total Routes" 
            value={routes.length}
            icon={<Route className="w-5 h-5" />}
          />
          <StatCard 
            label="With SEO Content" 
            value={routesWithSeo}
            icon={<FileText className="w-5 h-5" />}
            color="green"
          />
          <StatCard 
            label="From Locations" 
            value={fromLocations.length}
            icon={<MapPin className="w-5 h-5" />}
          />
          <StatCard 
            label="To Locations" 
            value={toLocations.length}
            icon={<MapPin className="w-5 h-5" />}
          />
          <StatCard 
            label="Vehicle Types" 
            value={vehicles.length}
            icon={<Car className="w-5 h-5" />}
          />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search routes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                data-testid="search-input"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">From:</label>
              <select
                value={fromFilter}
                onChange={(e) => setFromFilter(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white max-w-[180px]"
                data-testid="from-filter"
              >
                <option value="all">All Locations</option>
                {fromLocations.map(loc => (
                  <option key={loc.slug} value={loc.slug}>{loc.name}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">To:</label>
              <select
                value={toFilter}
                onChange={(e) => setToFilter(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white max-w-[180px]"
                data-testid="to-filter"
              >
                <option value="all">All Destinations</option>
                {toLocations.map(loc => (
                  <option key={loc.slug} value={loc.slug}>{loc.name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">Region:</label>
              <select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white max-w-[150px]"
                data-testid="region-filter"
              >
                <option value="all">All Regions</option>
                {availableRegions.map(region => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">Sort:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white max-w-[180px]"
                data-testid="sort-filter"
              >
                <option value="name">Name (A-Z)</option>
                <option value="distance-asc">Distance (Low → High)</option>
                <option value="distance-desc">Distance (High → Low)</option>
                <option value="price-asc">Price (Low → High)</option>
                <option value="price-desc">Price (High → Low)</option>
              </select>
            </div>
            
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>

            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleClearAllPrices} 
              disabled={clearingPrices}
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
              data-testid="clear-prices-btn"
            >
              {clearingPrices ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Clear Custom Prices
            </Button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
            {error}
          </div>
        )}

        {/* Routes Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="routes-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Route</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Distance</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Duration</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Starting From</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Vehicle Prices</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-12 text-center text-slate-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Loading routes...
                    </td>
                  </tr>
                ) : sortedRoutes.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-12 text-center text-slate-500">
                      No routes found
                    </td>
                  </tr>
                ) : (
                  sortedRoutes.map((route) => (
                    <tr key={route.route_id} className="hover:bg-slate-50" data-testid={`route-row-${route.route_id}`}>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-900 flex items-center gap-2">
                              <span className="truncate">{route.from_name}</span>
                              <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                              <span className="truncate">{route.to_name}</span>
                            </div>
                            <div className="text-xs text-slate-500 font-mono">{route.route_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-slate-700">{route.distance}</span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1 text-sm text-slate-700">
                          <Clock className="w-3 h-3 text-slate-400" />
                          {route.duration}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-lg font-bold text-orange-500">€{getMinPrice(route)}</span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1">
                          {(route.vehicle_prices || []).slice(0, 4).map(vp => (
                            <span 
                              key={vp.vehicle_id} 
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                                vp.is_custom 
                                  ? 'bg-orange-100 text-orange-700 font-medium' 
                                  : 'bg-slate-100 text-slate-600'
                              }`}
                              title={vp.is_custom ? 'Custom price' : 'Calculated price'}
                            >
                              €{vp.price}
                              {vp.is_custom && <span className="ml-0.5">★</span>}
                            </span>
                          ))}
                          {!route.vehicle_prices?.length && (
                            <span className="text-xs text-slate-400 italic">Not set</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditModal(route)}
                            data-testid={`edit-btn-${route.route_id}`}
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showModal && editingRoute && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="route-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold">Edit Route</h3>
                <p className="text-sm text-slate-500">
                  {editingRoute.from_name} → {editingRoute.to_name}
                </p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {formError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="distance">Distance</Label>
                  <Input
                    id="distance"
                    value={formData.distance}
                    placeholder="65 km"
                    data-testid="input-distance"
                    disabled
                    className="bg-slate-50 text-slate-500"
                  />
                  <p className="text-xs text-slate-400">From Google Maps API</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration">Duration</Label>
                  <Input
                    id="duration"
                    value={formData.duration}
                    placeholder="1h 15min"
                    data-testid="input-duration"
                    disabled
                    className="bg-slate-50 text-slate-500"
                  />
                  <p className="text-xs text-slate-400">From Google Maps API</p>
                </div>
              </div>

              {/* Vehicle Prices */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Car className="w-4 h-4" />
                  Vehicle Prices (€)
                </Label>
                <p className="text-xs text-slate-500">
                  Prices are calculated automatically using scales. Enter a custom price to override.
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-700">Custom ★</span>
                  <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-600">Calculated</span>
                </p>
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  {formData.vehicle_prices.map((vp) => (
                    <div key={vp.vehicle_id} className="flex items-center justify-between gap-4">
                      <span className="text-sm font-medium text-slate-700 flex-1">
                        {vp.vehicle_name}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400">€</span>
                        <Input
                          type="number"
                          value={vp.price}
                          onChange={(e) => handleVehiclePriceChange(vp.vehicle_id, e.target.value)}
                          className={`w-24 ${vp.is_custom ? 'border-orange-400 bg-orange-50' : ''}`}
                          data-testid={`price-${vp.vehicle_id}`}
                          placeholder="0"
                        />
                        {vp.is_custom ? (
                          <span className="text-xs text-orange-600 font-medium">★ Custom</span>
                        ) : (
                          <span className="text-xs text-slate-400">Calculated</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">
                  Set price to 0 or leave empty to use calculated price. The lowest price will be shown as &quot;starting from&quot; on the website.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Short Description</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of this route..."
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white resize-none"
                  data-testid="input-description"
                />
              </div>

              {/* SEO Section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                      <Search className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">SEO Settings</h4>
                      <p className="text-xs text-slate-500">Optimize this route for search engines</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => generateSeoForRoute(editingRoute.route_id)}
                    disabled={saving}
                    className="border-green-500 text-green-600 hover:bg-green-50"
                    data-testid="generate-seo-single-btn"
                  >
                    {saving ? (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3 mr-1" />
                        AI Generate
                      </>
                    )}
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="seo_title">SEO Title</Label>
                    <Input
                      id="seo_title"
                      value={formData.seo_title}
                      onChange={(e) => setFormData({...formData, seo_title: e.target.value})}
                      placeholder="e.g., Kalamata Airport to The Westin Transfer | From €80"
                      data-testid="input-seo-title"
                    />
                    <p className="text-xs text-slate-500">
                      {formData.seo_title?.length || 0}/60 characters (recommended)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="seo_description">SEO Meta Description</Label>
                    <textarea
                      id="seo_description"
                      value={formData.seo_description}
                      onChange={(e) => setFormData({...formData, seo_description: e.target.value})}
                      placeholder="e.g., Book private transfer from Kalamata Airport to The Westin Costa Navarino. Professional drivers, fixed prices, free cancellation..."
                      rows={2}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white resize-none text-sm"
                      data-testid="input-seo-description"
                    />
                    <p className="text-xs text-slate-500">
                      {formData.seo_description?.length || 0}/160 characters (recommended)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="seo_content">SEO Content (About This Route)</Label>
                    <textarea
                      id="seo_content"
                      value={formData.seo_content}
                      onChange={(e) => setFormData({...formData, seo_content: e.target.value})}
                      placeholder="Write unique, detailed content about this route. Include landmarks, journey highlights, local tips..."
                      rows={8}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white resize-none text-sm"
                      data-testid="input-seo-content"
                    />
                    <p className="text-xs text-slate-500">
                      This content appears in the &quot;About This Route&quot; section. Use line breaks to separate paragraphs. {formData.seo_content?.length || 0} characters
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-route-btn">
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Update Route
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Route Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="create-route-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold">Add New Route</h3>
                <p className="text-sm text-slate-500">Create a new transfer route between locations</p>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {formError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {formError}
              </div>
            )}

            <form onSubmit={handleCreateRoute} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="from_slug">From Location *</Label>
                <select
                  id="from_slug"
                  value={createFormData.from_slug}
                  onChange={(e) => setCreateFormData({...createFormData, from_slug: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white"
                  data-testid="select-from"
                >
                  <option value="">Select pickup location...</option>
                  {locations.filter(l => !l.is_hotel).map(loc => (
                    <option key={loc.slug} value={loc.slug}>{loc.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="to_slug">To Location *</Label>
                <select
                  id="to_slug"
                  value={createFormData.to_slug}
                  onChange={(e) => setCreateFormData({...createFormData, to_slug: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white"
                  data-testid="select-to"
                >
                  <option value="">Select destination...</option>
                  {locations.filter(l => l.is_hotel).map(loc => (
                    <option key={loc.slug} value={loc.slug}>{loc.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="create-distance">Distance</Label>
                  <Input
                    id="create-distance"
                    value={createFormData.distance}
                    onChange={(e) => setCreateFormData({...createFormData, distance: e.target.value})}
                    placeholder="65 km"
                    data-testid="input-create-distance"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-duration">Duration</Label>
                  <Input
                    id="create-duration"
                    value={createFormData.duration}
                    onChange={(e) => setCreateFormData({...createFormData, duration: e.target.value})}
                    placeholder="1h 15min"
                    data-testid="input-create-duration"
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                <strong>Note:</strong> After creating the route, you can set vehicle prices by clicking Edit on the route.
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="create-route-submit">
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Route
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

// Stat Card Component
function StatCard({ label, value, icon }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">
          {icon}
        </div>
        <div>
          <div className="text-2xl font-bold text-slate-900">{value}</div>
          <div className="text-xs text-slate-500">{label}</div>
        </div>
      </div>
    </div>
  );
}
